import React, { useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import {
  updateBriefProject,
  getBriefProject,
} from '@disco/state-management/reducers/entities/briefProjects'
import {
  Dialog,
  Button as DsButton,
  Flex as DsFlex,
  TextField,
  Text,
} from 'components/ds'

interface RenameProjectDialogProps {
  onOpenChange: (open: boolean) => void
  briefProjectId?: string
}

const RenameProjectDialog: React.FC<RenameProjectDialogProps> = ({
  onOpenChange,
  briefProjectId,
}) => {
  const dispatch = useDispatch()
  const briefProject = useSelector(state =>
    briefProjectId
      ? getBriefProject(state, { briefProjectId: Number(briefProjectId) })
      : undefined,
  )

  const [projectName, setProjectName] = useState(briefProject?.name || '')
  const isUpdating = briefProject?.updating || false

  const handleConfirm = async () => {
    if (
      briefProjectId &&
      projectName.trim() &&
      projectName !== briefProject?.name
    ) {
      try {
        await dispatch(
          updateBriefProject({
            briefProjectId: Number(briefProjectId),
            updates: { name: projectName.trim() },
          }),
        ).unwrap()
        onOpenChange(false)
      } catch (error) {
        // Error is handled by Redux action, dialog stays open
      }
    } else if (projectName === briefProject?.name) {
      // No change, just close dialog
      onOpenChange(false)
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  const isNameChanged = projectName.trim() !== briefProject?.name
  const isValidName = projectName.trim().length > 0

  return (
    <Dialog.Root defaultOpen onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="450px" aria-describedby={undefined}>
        <Dialog.Title>Rename brief project</Dialog.Title>

        <>
          <Text as="label" size="2" weight="medium">
            Name
          </Text>
          <TextField.Root
            size="2"
            disabled={isUpdating}
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && isValidName && isNameChanged) {
                handleConfirm()
              }
              if (e.key === 'Escape') {
                handleCancel()
              }
            }}
          />
        </>

        <DsFlex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <DsButton
              variant="soft"
              color="gray"
              size="2"
              disabled={isUpdating}
              onClick={handleCancel}
            >
              Cancel
            </DsButton>
          </Dialog.Close>
          <DsButton
            variant="solid"
            size="2"
            onClick={handleConfirm}
            disabled={isUpdating || !isValidName || !isNameChanged}
            loading={isUpdating}
          >
            {isUpdating ? 'Renaming...' : 'Rename'}
          </DsButton>
        </DsFlex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default RenameProjectDialog
